import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { filter, switchMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { select, Store } from '@ngrx/store';
import { MenuService } from 'src/app/services/menu';
import * as SettingsActions from 'src/app/store/settings/actions';
import * as Reducers from 'src/app/store/reducers';
import { combineLatest, Subscription } from 'rxjs';
import { ProtectedPrivacyService } from '@app/services/ui/protected-privacy.service';
import { EntryItem, MenuDataItem } from '@app/services/menu/config';
import { PermissionService } from '@app/services/permission/permission.service';

type MenuDataItemWithShow = (MenuDataItem & {show?: boolean});

@Component({
	selector: 'app-menu-left',
	templateUrl: './menu-left.component.html',
	styleUrls: ['./menu-left.component.scss'],
})

export class MenuLeftComponent implements OnInit, OnDestroy {
	
	@HostBinding('class.print-hide')
	public readonly printHide = true;
	
	menuColor: string;
	isMenuShadow: boolean;
	isMenuUnfixed: boolean;
	isMobileView: boolean;
	leftMenuWidth: number;
	leftMenuWidthCollapsed: number;
	isMenuCollapsed: boolean;
	menuData: MenuDataItemWithShow[] = [];
	permissions: string[];
	menuDataActivated: MenuDataItemWithShow[];
	private subscription = new Subscription();
	private routerEventUrl = this.router.url;

	constructor(private menuService: MenuService,
				private protectedPrivacyService: ProtectedPrivacyService,
				private store: Store<never>,
				private router: Router,
				private permissionService: PermissionService,
	) {
		this.subscription.add(this.store.pipe(
			select(Reducers.getUser),
			filter(state => !!state.username),
			switchMap(() => combineLatest([
				this.permissionService.permissions$,
				this.menuService.menuData$,
				protectedPrivacyService.toggleChange$(),
			])),
		).subscribe(([permissions, menuData,  ]) => {
			const menuDataWithShow = _.cloneDeep(menuData) as MenuDataItemWithShow[];
			for (const item of menuDataWithShow) {
				item.show = !item.permissions || item.permissions.some(permission => permissions.includes(permission));
			}
			this.menuData = menuDataWithShow;
			this.activateMenu(this.routerEventUrl, this.menuData);
		}));
		this.subscription.add(this.store.pipe(select(Reducers.getSettings)).subscribe(state => {
			this.menuColor = state.menuColor
			this.isMenuShadow = state.isMenuShadow
			this.isMenuUnfixed = state.isMenuUnfixed
			this.isMobileView = state.isMobileView
			this.leftMenuWidth = state.leftMenuWidth
			this.leftMenuWidthCollapsed = state.leftMenuWidthCollapsed
			this.isMenuCollapsed = state.isMenuCollapsed

		}));
	}

	ngOnInit(): void {
		this.activateMenu(this.router.url, this.menuData || []);
		this.subscription.add(this.router.events
			.pipe(filter(event => event instanceof NavigationStart))
			.subscribe((event: NavigationStart) => {
				this.routerEventUrl = event.url;
				this.activateMenu(this.routerEventUrl, this.menuData);
			}));
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	activateMenu(url: string, menuData: MenuDataItemWithShow[]): void {

		menuData = _.cloneDeep(menuData);
		if (url) {
			const indexOf = url?.indexOf('#');
			const trimmedUrl = url?.substr(0, indexOf > -1 ? indexOf : url.length);
			const pathWithSelection = this.getPath({ url: trimmedUrl }, menuData, (entry: any) => entry, 'url');
			if (pathWithSelection) {
				pathWithSelection.pop().selected = true;
				_.each(pathWithSelection, (parent: any) => (parent.open = true));
			}
		}
		this.menuDataActivated = menuData.slice();
		if (this.protectedPrivacyService.statusProtected) {
			for (const entry of this.menuDataActivated) {
				if (this.isEntryItem(entry) && entry.disableInProtectedPrivacy) {
					entry.disabled = true;
				}
			}
		}
	}

	private isEntryItem(item: MenuDataItem): item is EntryItem {
		return item && !item.category;
	}

	getPath<T, U>(
		urlObj: {url: string},
		source: T[],
		property: (e: T) => U,
		keyProperty = 'key',
		childrenProperty = 'children',
		path = [],
	): U[] | false {
		let found = undefined;
		const getElementChildren = (value: unknown) => _.get(value, childrenProperty);
		const getElementKey = (value: unknown) => _.get(value, keyProperty);
		const key = getElementKey(urlObj);
		return (
			_.some(source, (e: unknown) => {
				if (getElementKey(e) === key) {
					path.push(e);
					return true;
				} else {
					return (found = this.getPath(
						urlObj,
						getElementChildren(e),
						property,
						keyProperty,
						childrenProperty,
						path.concat(e),
					));
				}
			}) &&
			(found || _.map(path, property))
		);
	}

	onCollapse(value: any): void {
		this.store.dispatch(
			new SettingsActions.SetStateAction({
				isMenuCollapsed: value,
			}),
		)
	}
}
